/**
 * `CompendiumProvider` / `useCompendiumContext` — the **single** app-wide
 * {@link CompendiumState} for the whole frontend.
 *
 * `useCompendium` (`./compendium`) is a plain hook: each call owns its own
 * `useState` + an async effect that rebuilds the compendium from IndexedDB and
 * merges the GM's server-seeded world packs. **Calling it per consumer would run
 * the IDB read + world-pack fetch/merge once per call site**, with an independent
 * `loadingPersisted` lifecycle each.
 *
 * So this provider calls `useCompendium` **once** at the router root (sibling to
 * {@link GameSessionProvider}) and hands the resulting `CompendiumState` to every
 * consumer via context. The hook stays the underlying engine — the provider owns
 * the single instance.
 */
import { createContext, useContext, type ReactNode } from "react";
import { useCompendium, type CompendiumState } from "./compendium";

/**
 * **Exported for TESTS ONLY** — production code must never render this directly; use
 * {@link CompendiumProvider}, which owns the single async merge that is the whole point of this
 * module. The export exists because a component test needs to mount a *settled* state (a chosen
 * overlay with `loadingOverlay: false`) synchronously, and the real provider can only reach that
 * through an async IDB + fetch it has no seam to skip. Without it, a test of any overlay-derived
 * affordance can only ever observe the loading state — which would make an absence assertion
 * pass for the wrong reason, the hollow-negative class `CLAUDE.md` documents.
 */
export const CompendiumContext = createContext<CompendiumState | null>(null);

/** Provide the one app-wide compendium (single IDB + world-pack merge). */
export function CompendiumProvider({ children }: { children: ReactNode }) {
  const state = useCompendium();
  return <CompendiumContext.Provider value={state}>{children}</CompendiumContext.Provider>;
}

/**
 * Read the app-wide {@link CompendiumState}. Throws if used outside
 * {@link CompendiumProvider} — every compendium consumer goes through the one
 * provider (a single async merge), so a missing provider is a wiring bug, not a
 * runtime fallback.
 */
export function useCompendiumContext(): CompendiumState {
  const state = useContext(CompendiumContext);
  if (state === null) {
    throw new Error("useCompendiumContext must be used within a <CompendiumProvider>");
  }
  return state;
}
